import { supabase } from '../lib/supabase'
import { callEdgeFunction } from '../config/edge-functions.config'
import type {
  Video,
  VideoUpdate,
  VideoVisibility,
  User,
} from '../types/database.types'
import type { VideoFilters, Pagination } from '../stores/videoStore'

// Video with provider info joined
export interface VideoWithProvider extends Video {
  user: Pick<User, 'id' | 'full_name' | 'avatar_url' | 'specialty' | 'bio'> | null
}

// Response from video upload edge function
interface VideoUploadResponse {
  uploadUrl: string
  uploadId: string
  videoId: string
}

export const videoService = {
  /**
   * Get public videos with filters and pagination
   */
  async getPublicVideos(
    filters: VideoFilters,
    pagination: Pagination
  ): Promise<{ videos: VideoWithProvider[]; total: number }> {
    let query = supabase
      .from('videos')
      .select(
        `
        *,
        user:users!videos_user_id_fkey (
          id,
          full_name,
          avatar_url,
          specialty,
          bio
        )
      `,
        { count: 'exact' }
      )
      .eq('visibility', 'public')
      .eq('status', 'ready')

    // Apply search filter
    if (filters.search) {
      query = query.or(
        `title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
      )
    }

    // Apply category filter
    if (filters.category) {
      query = query.eq('category', filters.category)
    }

    // Apply tags filter
    if (filters.tags.length > 0) {
      query = query.contains('tags', filters.tags)
    }

    // Apply sorting
    switch (filters.sortBy) {
      case 'popular':
        query = query.order('view_count', { ascending: false })
        break
      case 'oldest':
        query = query.order('created_at', { ascending: true })
        break
      case 'title':
        query = query.order('title', { ascending: true })
        break
      case 'newest':
      default:
        query = query.order('created_at', { ascending: false })
    }

    // Apply pagination
    const from = (pagination.page - 1) * pagination.pageSize
    const to = from + pagination.pageSize - 1
    query = query.range(from, to)

    const { data, error, count } = await query

    if (error) throw error

    return {
      videos: data as VideoWithProvider[],
      total: count ?? 0,
    }
  },

  /**
   * Get a single video by ID
   */
  async getVideoById(id: string): Promise<VideoWithProvider | null> {
    const { data, error } = await supabase
      .from('videos')
      .select(
        `
        *,
        user:users!videos_user_id_fkey (
          id,
          full_name,
          avatar_url,
          specialty,
          bio
        )
      `
      )
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      throw error
    }

    return data as VideoWithProvider
  },

  /**
   * Get videos for the current user (provider's own videos)
   */
  async getMyVideos(userId: string): Promise<Video[]> {
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'ready')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data
  },

  /**
   * Get videos by a specific provider (public profile view)
   */
  async getVideosByProvider(
    providerId: string,
    includePrivate = false
  ): Promise<Video[]> {
    let query = supabase
      .from('videos')
      .select('*')
      .eq('user_id', providerId)
      .eq('status', 'ready')
      .order('created_at', { ascending: false })

    if (!includePrivate) {
      query = query.eq('visibility', 'public')
    }

    const { data, error } = await query

    if (error) throw error
    return data
  },

  /**
   * Get featured videos for homepage
   */
  async getFeaturedVideos(limit = 6): Promise<VideoWithProvider[]> {
    const { data, error } = await supabase
      .from('videos')
      .select(
        `
        *,
        user:users!videos_user_id_fkey (
          id,
          full_name,
          avatar_url,
          specialty,
          bio
        )
      `
      )
      .eq('visibility', 'public')
      .eq('status', 'ready')
      .eq('is_featured', true)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data as VideoWithProvider[]
  },

  /**
   * Create a video upload URL (calls edge function)
   */
  async createUploadUrl(
    title: string,
    description?: string,
    visibility: VideoVisibility = 'private'
  ): Promise<VideoUploadResponse> {
    const session = await supabase.auth.getSession()
    const token = session.data.session?.access_token

    if (!token) {
      throw new Error('Must be authenticated to upload videos')
    }

    return callEdgeFunction<VideoUploadResponse>('video-upload', {
      method: 'POST',
      body: { title, description, visibility },
      token,
    })
  },

  /**
   * Update video metadata
   */
  async updateVideo(id: string, updates: VideoUpdate): Promise<Video> {
    const { data, error } = await supabase
      .from('videos')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Delete a video
   */
  async deleteVideo(id: string): Promise<void> {
    const { error } = await supabase.from('videos').delete().eq('id', id)
    if (error) throw error
  },

  /**
   * Like a video
   */
  async likeVideo(videoId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('video_likes')
      .insert({ video_id: videoId, user_id: userId })

    if (error) {
      // Ignore duplicate likes
      if (error.code === '23505') return
      throw error
    }
  },

  /**
   * Unlike a video
   */
  async unlikeVideo(videoId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('video_likes')
      .delete()
      .eq('video_id', videoId)
      .eq('user_id', userId)

    if (error) throw error
  },

  /**
   * Check if user has liked a video
   */
  async hasLiked(videoId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('video_likes')
      .select('id')
      .eq('video_id', videoId)
      .eq('user_id', userId)
      .maybeSingle()

    if (error) throw error
    return !!data
  },

  /**
   * Log a video view (unique per user/session)
   * Uses upsert to silently ignore duplicate views
   */
  async logView(
    videoId: string,
    userId: string | null,
    sessionId: string,
    durationSeconds?: number,
    completed?: boolean
  ): Promise<void> {
    const { error } = await supabase.from('video_views').insert({
      video_id: videoId,
      user_id: userId,
      session_id: sessionId,
      view_duration_seconds: durationSeconds,
      completed,
    })

    // Silently ignore duplicate view errors (unique constraint violations)
    // Error code 23505 = unique_violation in PostgreSQL
    if (error && error.code !== '23505') {
      console.warn('Failed to log view:', error.message)
    }
  },

  /**
   * Get available categories (distinct from videos)
   */
  async getCategories(): Promise<string[]> {
    const { data, error } = await supabase
      .from('videos')
      .select('category')
      .eq('visibility', 'public')
      .eq('status', 'ready')
      .not('category', 'is', null)

    if (error) throw error

    // Get unique categories
    const categories = [...new Set(data.map((v) => v.category).filter(Boolean))]
    return categories as string[]
  },

  /**
   * Get available tags (common tags from public videos)
   */
  async getTags(): Promise<string[]> {
    const { data, error } = await supabase
      .from('videos')
      .select('tags')
      .eq('visibility', 'public')
      .eq('status', 'ready')

    if (error) throw error

    // Flatten and get unique tags
    const allTags = data.flatMap((v) => v.tags ?? [])
    const uniqueTags = [...new Set(allTags)]
    return uniqueTags.sort()
  },

  /**
   * Get a direct upload URL from Mux (for recording flow)
   * Returns upload URL that accepts raw video data
   */
  async getUploadUrl(): Promise<{ uploadUrl: string; videoId: string }> {
    const session = await supabase.auth.getSession()
    const token = session.data.session?.access_token

    if (!token) {
      throw new Error('Must be authenticated to upload videos')
    }

    const response = await callEdgeFunction<VideoUploadResponse>('video-upload', {
      method: 'POST',
      body: {
        title: 'Recording', // Placeholder, will be updated
        visibility: 'private',
        directUpload: true, // Flag for direct upload mode
      },
      token,
    })

    return {
      uploadUrl: response.uploadUrl,
      videoId: response.videoId,
    }
  },

  /**
   * Update video details after recording upload
   */
  async createVideoFromUpload(data: {
    mux_upload_id: string
    title: string
    user_id: string
    description?: string
    visibility?: VideoVisibility
  }): Promise<Video> {
    // First, find the video by mux upload ID
    const { data: existingVideo, error: findError } = await supabase
      .from('videos')
      .select('id')
      .eq('mux_upload_id', data.mux_upload_id)
      .single()

    if (findError && findError.code !== 'PGRST116') {
      throw findError
    }

    if (existingVideo) {
      // Update the existing video record
      const { data: updated, error: updateError } = await supabase
        .from('videos')
        .update({
          title: data.title,
          description: data.description || null,
          visibility: data.visibility || 'private',
        })
        .eq('id', existingVideo.id)
        .select()
        .single()

      if (updateError) throw updateError
      return updated
    }

    // If no existing video, create a new one (fallback)
    const { data: newVideo, error: createError } = await supabase
      .from('videos')
      .insert({
        user_id: data.user_id,
        title: data.title,
        description: data.description || null,
        visibility: data.visibility || 'private',
        mux_upload_id: data.mux_upload_id,
        status: 'processing',
      })
      .select()
      .single()

    if (createError) throw createError
    return newVideo
  },
}
